/**
 * Presentational pieces for the Google Meet Host Select — per-option row and
 * trigger value.
 *
 * The Google-Meet-external-host feature restricts the host to the picked
 * interviewers + the scheduler (same rule as Zoom — see `HostRenders`), but the
 * eligibility signal is different: a person can host only if they have granted
 * Kula the Google Meet scope. The caller (`buildGmeetHostField`) sets
 * `meetEligible` on each option from the `active_hosts?type=gmeet` id set; an
 * ineligible person is shown but disabled with a "No Google Meet access" chip,
 * mirroring Zoom's can't-host rows.
 */
import {
  Avatar,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type SelectOption,
} from '@kula-ai/components';
import { Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';

const REAUTH_TAG_TOOLTIP =
  'This user needs to connect/refresh their Google Calendar connection to host Google Meet interviews';

/** "Action needed" chip with an on-hover explanation, shown for a host who
 *  hasn't connected/refreshed their Google Calendar connection needed to host a Meet. */
function reauthGoogleCalTag() {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Tag label="Action needed" variant="error" size="sm" />
          </span>
        </TooltipTrigger>
        {/* Beats the Select menu's own portal (zIndex 9999999) — this row
            renders inside it. Same value as ScheduleAll's identical fix. */}
        <TooltipContent side="top" style={{ zIndex: 99999999 }}>
          {REAUTH_TAG_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export interface GmeetHostOptionData {
  /** False ⇒ the person has not granted the Google Meet scope → disabled + chip. */
  meetEligible?: boolean;
  image?: string;
}

/** `true` when the person behind the option can NOT host a Google Meet. */
export function isGmeetHostOptionDisabled(
  meta: GmeetHostOptionData | undefined
): boolean {
  return meta?.meetEligible === false;
}

function getMeta(
  option: SelectOption | undefined
): GmeetHostOptionData | undefined {
  if (!option) return undefined;
  const flat = option as unknown as GmeetHostOptionData;
  if (flat.meetEligible !== undefined || flat.image) return flat;
  return option.data as GmeetHostOptionData | undefined;
}

export function renderGmeetHostOption(
  option: SelectOption,
  ctx: { isSelected: boolean }
) {
  const meta = getMeta(option);
  const disabled = isGmeetHostOptionDisabled(meta);
  const image =
    (option as { image?: string }).image ?? meta?.image ?? undefined;
  return (
    <Flex
      w="100%"
      alignItems="center"
      gap="3"
      opacity={disabled ? 0.5 : 1}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      data-selected={ctx.isSelected ? 'true' : undefined}
    >
      <Avatar src={image} name={option.label} size="sm" shape="square" />
      <KText
        flex="1"
        minW={0}
        variant="small_text"
        fontSize="sm"
        color="secondary-900"
        fontWeight="medium"
        noOfLines={1}
      >
        {option.label}
      </KText>
      {disabled && reauthGoogleCalTag()}
    </Flex>
  );
}

export function renderGmeetHostTriggerValue(
  selected: SelectOption | SelectOption[] | null | undefined
) {
  const opt = Array.isArray(selected) ? selected[0] : selected;
  if (!opt) return null;
  const meta = getMeta(opt);
  const disabled = isGmeetHostOptionDisabled(meta);
  const image = (opt as { image?: string }).image ?? meta?.image ?? undefined;
  return (
    <Flex w="100%" alignItems="center" gap="2" minW={0}>
      <Avatar src={image} name={opt.label} size="xs" shape="square" />
      <KText
        flex="1"
        minW={0}
        variant="small_text"
        fontSize="sm"
        color="secondary-900"
        fontWeight="medium"
        lineHeight="shorter"
        noOfLines={1}
      >
        {opt.label}
      </KText>
      {disabled && reauthGoogleCalTag()}
    </Flex>
  );
}
